import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { fitImageStyle } from '../../components/image/useFullscreenStage';
import { detectDocumentCorners } from '../flatten/flattenDocumentEngine';
import { isConvexQuad } from '../shared/perspectiveHomography';

const HANDLE = 9;
const STROKE = 2;
const FILL = 'rgba(0, 120, 255, 0.12)';

/** @typedef {[number, number]} Pt */

/** Fallback quad when auto-detection finds nothing: a centred 90% box. */
function defaultCorners(nw, nh) {
    const mx = nw * 0.05;
    const my = nh * 0.05;
    return [
        [mx, my],
        [nw - mx, my],
        [nw - mx, nh - my],
        [mx, nh - my],
    ];
}

/**
 * Perspective crop overlay — auto-detects the page edges on load and lets the
 * user drag the four corners (or the whole quad).
 *
 * It hands back the QUAD, never pixels: the page keeps the untouched original and
 * re-derives the working image from it, so a crop can always be reopened and
 * widened instead of being baked in one way.
 */
const DocumentCleanupCropPanel = forwardRef(function DocumentCleanupCropPanel(
    { imageSrc, initialQuad, onLoaded, fitBox },
    ref,
) {
    const imgRef = useRef(null);
    const [nw, setNw] = useState(0);
    const [nh, setNh] = useState(0);
    const [box, setBox] = useState({ w: 0, h: 0 });

    /** @type {React.MutableRefObject<Pt[]>} */
    const cornersRef = useRef([
        [0, 0],
        [100, 0],
        [100, 100],
        [0, 100],
    ]);
    const [, bump] = useState(0);
    const force = () => bump((x) => x + 1);

    const dragRef = useRef(null);
    const layoutRef = useRef({ scale: 1 });

    const measure = useCallback(() => {
        const img = imgRef.current;
        if (!img?.naturalWidth) return;
        const rect = img.getBoundingClientRect();
        layoutRef.current = { scale: rect.width / img.naturalWidth };
        setBox({ w: rect.width, h: rect.height });
    }, []);

    const natToDisp = useCallback(([x, y]) => {
        const { scale } = layoutRef.current;
        return [x * scale, y * scale];
    }, []);

    const dispToNat = useCallback(
        ([x, y]) => {
            const { scale } = layoutRef.current;
            return [
                Math.max(0, Math.min(nw || 1, x / scale)),
                Math.max(0, Math.min(nh || 1, y / scale)),
            ];
        },
        [nw, nh],
    );

    const onImgLoad = useCallback(() => {
        const img = imgRef.current;
        if (!img) return;
        const w = img.naturalWidth;
        const h = img.naturalHeight;
        setNw(w);
        setNh(h);

        // A quad the user already chose wins over a fresh detection — reopening
        // the crop must show the crop they made, not start over.
        let detected = null;
        if (initialQuad?.length === 4) {
            detected = initialQuad.map(([x, y]) => [
                Math.max(0, Math.min(w, x)),
                Math.max(0, Math.min(h, y)),
            ]);
        } else {
            try {
                const corners = detectDocumentCorners(img, w, h);
                if (corners?.length === 4) {
                    detected = corners.map((p) => [
                        Math.max(0, Math.min(w, p.x)),
                        Math.max(0, Math.min(h, p.y)),
                    ]);
                }
            } catch {
                detected = null;
            }
        }

        cornersRef.current = detected && isConvexQuad(detected) ? detected : defaultCorners(w, h);
        requestAnimationFrame(() => {
            measure();
            force();
            onLoaded?.();
        });
    }, [measure, onLoaded, initialQuad]);

    useEffect(() => {
        const img = imgRef.current;
        if (!img) return undefined;
        const ro = new ResizeObserver(() => {
            measure();
            force();
        });
        ro.observe(img);
        return () => ro.disconnect();
    }, [imageSrc, measure]);

    const dragMatchesPointer = useCallback((e, d) => {
        if (!d) return false;
        if (d.pointerType === 'mouse' && e.pointerType === 'mouse') return true;
        return d.pointerId === e.pointerId;
    }, []);

    useEffect(() => {
        const onMove = (e) => {
            const d = dragRef.current;
            if (!d || !dragMatchesPointer(e, d)) return;
            const img = imgRef.current;
            if (!img) return;

            const rect = img.getBoundingClientRect();
            const lx = e.clientX - rect.left;
            const ly = e.clientY - rect.top;

            if (d.kind === 'corner') {
                cornersRef.current[d.cornerIndex] = dispToNat([lx, ly]);
            } else if (d.kind === 'pan') {
                const current = dispToNat([lx, ly]);
                const dx = current[0] - d.startNat[0];
                const dy = current[1] - d.startNat[1];
                cornersRef.current = d.startCorners.map(([x, y]) => [
                    Math.max(0, Math.min((nw || 1) - 1, x + dx)),
                    Math.max(0, Math.min((nh || 1) - 1, y + dy)),
                ]);
            }
            force();
        };

        const onUp = (e) => {
            const d = dragRef.current;
            if (!d || !dragMatchesPointer(e, d)) return;
            dragRef.current = null;
        };

        document.addEventListener('pointermove', onMove);
        document.addEventListener('pointerup', onUp);
        document.addEventListener('pointercancel', onUp);
        return () => {
            document.removeEventListener('pointermove', onMove);
            document.removeEventListener('pointerup', onUp);
            document.removeEventListener('pointercancel', onUp);
        };
    }, [dispToNat, dragMatchesPointer, nw, nh]);

    const onPointerDownHandle = useCallback((cornerIndex) => (e) => {
        e.preventDefault();
        e.stopPropagation();
        dragRef.current = {
            kind: 'corner',
            cornerIndex,
            pointerId: e.pointerId,
            pointerType: e.pointerType,
        };
        try {
            e.currentTarget.setPointerCapture(e.pointerId);
        } catch {
            /* ignore */
        }
    }, []);

    const onPointerDownPan = useCallback(
        (e) => {
            if (e.pointerType === 'mouse' && e.button !== 0) return;
            e.preventDefault();
            const img = imgRef.current;
            if (!img || !nw || !nh) return;
            const rect = img.getBoundingClientRect();
            dragRef.current = {
                kind: 'pan',
                pointerId: e.pointerId,
                pointerType: e.pointerType,
                startNat: dispToNat([e.clientX - rect.left, e.clientY - rect.top]),
                startCorners: cornersRef.current.map((p) => [...p]),
            };
            try {
                e.currentTarget.setPointerCapture(e.pointerId);
            } catch {
                /* ignore */
            }
        },
        [dispToNat, nw, nh],
    );

    useImperativeHandle(
        ref,
        () => ({
            /** Reset the quad back to the full frame. */
            selectWholeImage() {
                const img = imgRef.current;
                if (!img?.naturalWidth) return;
                cornersRef.current = [
                    [0, 0],
                    [img.naturalWidth, 0],
                    [img.naturalWidth, img.naturalHeight],
                    [0, img.naturalHeight],
                ];
                force();
            },
            /** The chosen quad, in the source image's own pixel coordinates. */
            getQuad() {
                const img = imgRef.current;
                if (!img?.naturalWidth) throw new Error('no image');
                const quad = cornersRef.current.map((p) => [...p]);
                if (!isConvexQuad(quad)) throw new Error('invalid quad');
                return quad;
            },
        }),
        [],
    );

    const corners = cornersRef.current;
    const points = corners.map((p) => natToDisp(p));
    const polygonPoints = points.map((p) => `${p[0]},${p[1]}`).join(' ');

    return (
        <div className="relative inline-block max-w-full select-none touch-none">
            <img
                ref={imgRef}
                src={imageSrc}
                alt=""
                draggable={false}
                onLoad={onImgLoad}
                // Fitted to the modal's measured stage, aspect ratio kept — the
                // whole photo in view, nothing to scroll (see useFullscreenStage).
                style={fitImageStyle(fitBox)}
                className="block object-contain"
            />
            {nw > 0 && nh > 0 && box.w > 0 ? (
                <svg
                    width={box.w}
                    height={box.h}
                    className="pointer-events-none absolute left-0 top-0 overflow-visible"
                >
                    <polygon
                        points={polygonPoints}
                        fill={FILL}
                        stroke="#0288d1"
                        strokeWidth={STROKE}
                        style={{ pointerEvents: 'visiblePainted', cursor: 'move' }}
                        onPointerDown={onPointerDownPan}
                    />
                    {points.map((p, i) => (
                        <circle
                            key={i}
                            cx={p[0]}
                            cy={p[1]}
                            r={HANDLE}
                            fill="#fff"
                            stroke="#ff9800"
                            strokeWidth={2}
                            style={{ pointerEvents: 'all', cursor: 'grab' }}
                            onPointerDown={onPointerDownHandle(i)}
                        />
                    ))}
                </svg>
            ) : null}
        </div>
    );
});

export default DocumentCleanupCropPanel;
